import * as React from 'react'

export const DEFAULT_Z_INDEX = 9999

/* TYPES */

interface ZIndexConfig {
  initialValue?: number
}

interface ZIndexApi {
  increment: () => void
  value: number
}

interface Props extends ZIndexConfig {
  children?: React.ReactNode
}

interface RenderProps extends ZIndexConfig {
  children: (props: ZIndexApi) => {}
}

/* HOOK */

export const useZIndex = ({
  initialValue = DEFAULT_Z_INDEX,
}: ZIndexConfig): ZIndexApi => {
  const [value, setValue] = React.useState(initialValue)

  const increment = () => setValue(value + 1)

  return {
    increment,
    value,
  }
}

/* DECORATOR */

export const withZIndex = <T extends {}>(config: ZIndexConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { zIndex: ZIndexConfig }) => (
  <Component {...props} zIndex={useZIndex({ ...config, ...props.zIndex })} />
)

/* RENDER PROP */

export const ZIndex = ({ children, ...props }: RenderProps) => {
  const zIndex = useZIndex(props)

  return typeof children === 'function' ? children(zIndex) : children
}

/* CONTEXT */

const noop = (method: string) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<ZIndexProvider>\``
  )

export const ZIndexContext = React.createContext<ZIndexApi>({
  increment: noop('increment'),
  value: DEFAULT_Z_INDEX,
})

export const ZIndexProvider = ({ children, ...props }: Props) => (
  <ZIndexContext.Provider value={useZIndex(props)}>
    {children}
  </ZIndexContext.Provider>
)

export const consumeZIndex = () => React.useContext(ZIndexContext)
